import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { PaymentsService } from '../../services/payments.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';

/**
 * Vendor-Backed Remittance Upload.
 * Three-step flow inside a mat-stepper:
 *   1. Pick vendor + drop CSV/XLSX
 *   2. Map columns (their column → our field)
 *   3. Preview matched / partial / unmatched rows, then commit
 *
 * Commit fans out matched rows through the server's existing nationalaccount
 * endpoint, one cross-ledger offset per invoice.
 */
@Component({
	selector: 'app-vendor-backed-remittance',
	templateUrl: './vendor-backed-remittance.component.html',
	styleUrls: ['./vendor-backed-remittance.component.scss']
})
export class VendorBackedRemittanceComponent implements OnInit {
	step = 1;
	uploading = false;
	previewing = false;
	committing = false;

	fileForm: UntypedFormGroup;
	commitForm: UntypedFormGroup;

	// File state
	fileName = '';
	fileContentBase64 = '';
	availableColumns: string[] = [];

	// Mapping (their column → our field). UI keys our field, value is the picked column.
	mapping = {
		order_ref: '',
		vendor_reference: '',
		vendor_amount: '',
		commission_amount: '',
		settlement_id: '',
	};

	// Preview state
	preview: any = null;
	postingResult: any = null;

	// Page state we share with the user
	payment_methods: any[] = [];
	bankaccounts: any[] = [];
	currencies: any[] = [];

	constructor(
		private fb: UntypedFormBuilder,
		private paymentsService: PaymentsService,
		private globalSearchService: GlobalSearchService,
	) {}

	ngOnInit(): void {
		this.fileForm = this.fb.group({
			vendor_id: ['', Validators.required],
			vendor_name: [''],
		});
		this.commitForm = this.fb.group({
			payment_type: ['', Validators.required],
			gl_account: ['', Validators.required],
			bankaccount: ['', Validators.required],
			currency: ['', Validators.required],
			postdate: [new Date().toISOString().substring(0, 10), Validators.required],
			tag: [''],
		});

		this.globalSearchService.payment_methods.subscribe(pm => {
			this.payment_methods = (pm || []).filter((m: any) => Number(m.national_account) === 1);
		});
		this.globalSearchService.bankaccounts.subscribe(b => { this.bankaccounts = b || []; });
		this.globalSearchService.currencies.subscribe(c => { this.currencies = c || []; });
		this.globalSearchService.company.subscribe(c => {
			if (c && c.currencydefault) { this.commitForm.get('currency').setValue(c.currencydefault); }
		});

		// When the operator picks a payment method, seed GL from the row config
		this.commitForm.get('payment_type').valueChanges.subscribe(pid => {
			const m = this.payment_methods.find((x: any) => x.paymentid == pid);
			if (m && m.gl_account && !this.commitForm.get('gl_account').value) {
				this.commitForm.get('gl_account').setValue(m.gl_account);
			}
		});
	}

	onVendorPicked(vendor: any) {
		if (vendor && (vendor.supplierid || vendor.supplier_id)) {
			this.fileForm.get('vendor_id').setValue(vendor.supplierid || vendor.supplier_id);
			this.fileForm.get('vendor_name').setValue(vendor.suppname || vendor.name || '');
		}
	}

	onFilePicked(ev: any) {
		const file: File = ev?.target?.files?.[0];
		if (!file) { return; }
		this.fileName = file.name;
		const reader = new FileReader();
		reader.onload = () => {
			const result = reader.result as string;
			// strip data:... prefix
			const comma = result.indexOf(',');
			this.fileContentBase64 = comma >= 0 ? result.substring(comma + 1) : result;
			this.parseHeaders();
		};
		reader.readAsDataURL(file);
	}

	/**
	 * Light client-side header parse from CSV so the mapping step has options.
	 * Server re-parses authoritatively during preview.
	 */
	parseHeaders() {
		if (!this.fileName.toLowerCase().endsWith('.csv') || !this.fileContentBase64) {
			// For XLSX, we won't preview headers client-side — operator types or
			// we surface them after the server's preview call (out-of-scope for MVP).
			this.availableColumns = [];
			return;
		}
		try {
			const decoded = atob(this.fileContentBase64);
			const firstLine = decoded.split(/\r?\n/)[0] || '';
			this.availableColumns = firstLine.split(',').map(s => s.replace(/^"|"$/g, '').trim()).filter(s => !!s);
			// Auto-guess obvious mappings
			this.guessMapping();
		} catch {
			this.availableColumns = [];
		}
	}

	guessMapping() {
		const hints = {
			order_ref: /order|po|invoice|customer/i,
			vendor_reference: /sticker|vendor.?ref|cardref|auth/i,
			vendor_amount: /wholesale|vendor.?amt|relief|net.?paid/i,
			commission_amount: /commission|markup|over.?short/i,
			settlement_id: /settlement|batch|deposit/i,
		};
		for (const col of this.availableColumns) {
			for (const key of Object.keys(hints)) {
				if (!this.mapping[key] && hints[key].test(col)) {
					this.mapping[key] = col;
				}
			}
		}
	}

	goToMapping() {
		if (!this.fileForm.valid || !this.fileContentBase64) {
			alert('Pick a vendor and a file first');
			return;
		}
		this.step = 2;
	}

	goToPreview() {
		if (!this.mapping.order_ref || !this.mapping.vendor_amount) {
			alert('At minimum map the Order # and Vendor relief amount columns');
			return;
		}
		this.previewing = true;
		const payload = {
			vendor_id: this.fileForm.get('vendor_id').value,
			file: { name: this.fileName, content: this.fileContentBase64 },
			mapping: this.mapping,
		};
		this.paymentsService.previewRemittance(payload).subscribe((r: any) => {
			this.previewing = false;
			if (r?.success) {
				this.preview = r;
				this.step = 3;
			} else {
				alert(r?.message || 'Preview failed');
			}
		});
	}

	commit(onlyMatched: boolean) {
		if (!this.commitForm.valid) {
			alert('Pick payment method, GL, bank, currency, and date');
			return;
		}
		const rows = onlyMatched ? (this.preview?.matched || []) : ((this.preview?.matched || []).concat(this.preview?.partial || []));
		if (!rows.length) { alert('No rows to post'); return; }

		this.committing = true;
		const payload = {
			vendor_id: this.fileForm.get('vendor_id').value,
			gl_account: this.commitForm.get('gl_account').value,
			payment_type: this.commitForm.get('payment_type').value,
			bankaccount: this.commitForm.get('bankaccount').value,
			currency: this.commitForm.get('currency').value,
			postdate: this.commitForm.get('postdate').value,
			tag: this.commitForm.get('tag').value,
			rows,
		};
		this.paymentsService.commitRemittance(payload).subscribe((r: any) => {
			this.committing = false;
			this.postingResult = r;
			if (r?.success) {
				this.globalSearchService.showNotification(
					`Posted ${r.totals?.posted ?? 0} payments`, 'success', 'bottom', 'left'
				);
			} else {
				alert('Some rows failed — see results below');
			}
		});
	}

	reset() {
		this.step = 1;
		this.fileForm.reset();
		this.commitForm.reset();
		this.fileName = '';
		this.fileContentBase64 = '';
		this.availableColumns = [];
		this.mapping = { order_ref: '', vendor_reference: '', vendor_amount: '', commission_amount: '', settlement_id: '' };
		this.preview = null;
		this.postingResult = null;
	}
}
